const COLLECTION = process.env.REACT_APP_COLLECTION || null;

export const colors = COLLECTION === 'aleksandrowicz' ? {
  1: '#1b4b67',
  2: '#f28cb1',
  3: '#9932cc',
  default: '#1b4b67',
  background: '#F5F5F5'
} : COLLECTION === 'scholten' ? {
  1: '#11b4da',
  2: '#f28cb1',
  3: '#9932cc',
  default: '#007FAD',
  background: '#F5F5F5'
} : COLLECTION === 'taycm' ? {
  // Tel Aviv-Yafo City Museum pilot: deep indigo, taken from the museum's own
  // logo. Follows the same relationship the other collections use — `default`
  // matches the header, and the photo marker is a lighter, more saturated tone
  // of the same hue so arrows stay legible against the map (5.5:1 on #F5F5F5).
  1: '#5b52c9',
  2: '#f28cb1',
  3: '#9932cc',
  default: '#2C267A',
  background: '#F5F5F5'
} : {
  // Unified site: slate-blue family, distinct from both per-collection palettes
  1: '#5b8da8',
  2: '#f28cb1',
  3: '#9932cc',
  default: '#3a5a6e',
  background: '#F5F5F5'
}

/**
 * The site's accent colour, for UI chrome: spinners, selection outlines,
 * checked checkboxes, active filter icons, login glyphs.
 *
 * This used to be Scholten's #007FAD hardcoded in ~24 places across ten
 * components, so every deployment — Aleksandrowicz, the unified site, TAYCM —
 * showed Scholten blue in its widgets no matter what its own palette said.
 * That is what made a non-Scholten site look incoherent.
 *
 * Keyed to `colors.default`, so a collection's chrome always matches its header.
 */
export const accent = colors.default;

// --- Derived tones -------------------------------------------------------
//
// The rest of the UI chrome is generated from the accent's HUE, so a collection
// only ever declares its own colours above and everything else follows. Before
// this, the widget backgrounds (cadet blue), modal link text (cyan) and MUI's
// default blue were fixed values that stayed Scholten-ish on every site.

function hexToHsl(hex) {
  const n = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(n.slice(i, i + 2), 16) / 255);
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h = max === r ? ((g - b) / d + (g < b ? 6 : 0))
          : max === g ? (b - r) / d + 2
          : (r - g) / d + 4;
  return [h / 6, s, l];
}

function hslToHex(h, s, l) {
  const f = (n) => {
    const k = (n + h * 12) % 12;
    const a = s * Math.min(l, 1 - l);
    const v = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(v * 255).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/** Same hue as the accent, at a given lightness/saturation. */
function tone(lightness, saturation) {
  const [h, s] = hexToHsl(accent);
  return hslToHex(h, saturation == null ? s : saturation, lightness);
}

/**
 * Link and caption text inside the item modal, which sits on a dark backdrop.
 * Lightness 0.75 keeps this above 4.5:1 even for the darkest accent hues —
 * indigo needs more lift than cyan to stay legible.
 */
export const accentText = tone(0.75, 0.55);

/** Tinted panel background for the homeless / search / location widgets. */
export const accentSurface = tone(0.93, 0.30);

/** Slightly stronger tint, for headers and toolbars inside those panels. */
export const accentSurfaceStrong = tone(0.88, 0.32);
